import React, { useState, useMemo } from 'react';
import { Modal, Checkbox, Collapse, Button } from 'antd';
import s from './s.module.less';
import { TInsuranceInput } from 'types/common';

const { Panel } = Collapse;

interface InsuranceSelectionModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (carrier: string[], plans: Record<string, string[]>) => void;
    insuranceList: TInsuranceInput[];
    state: string;
    planWithCarrierName?: boolean;
    splitsymbol?: string;
}

const InsuranceSelectionModal: React.FC<InsuranceSelectionModalProps> = ({
    visible,
    onCancel,
    onSave,
    insuranceList,
    state,
    planWithCarrierName,
    splitsymbol = ':',
}) => {
    const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
    const [selectedPlans, setSelectedPlans] = useState<Record<string, string[]>>({});
    const [expandedCarriers, setExpandedCarriers] = useState<string[]>([]);

    // 获取所有保险公司列表并按字母排序
    const sortedCarriers = useMemo(() => {
        const carriers = insuranceList.map((item) => item.carrier).sort();
        const midPoint = Math.ceil(carriers.length / 2);
        return {
            leftColumn: carriers.slice(0, midPoint),
            rightColumn: carriers.slice(midPoint),
        };
    }, [insuranceList]);

    const handleCarrierSelect = (carrier: string, checked: boolean) => {
        const newSelectedCarriers = checked
            ? [...selectedCarriers, carrier]
            : selectedCarriers.filter((c) => c !== carrier);
        setSelectedCarriers(newSelectedCarriers);

        if (!checked) {
            const newPlans = { ...selectedPlans };
            delete newPlans[carrier];
            setSelectedPlans(newPlans);
        }
    };

    const handlePlanSelect = (carrier: string, plan: string, checked: boolean) => {
        const currentPlans = selectedPlans[carrier] || [];
        const newPlans = checked
            ? [...currentPlans, plan]
            : currentPlans.filter((p) => p !== plan);

        setSelectedPlans((prev) => ({
            ...prev,
            [carrier]: newPlans,
        }));
    };

    const handleSelectAllPlans = (carrier: string) => {
        const allPlans = insuranceList.find((i) => i.carrier === carrier)?.plans || [];
        setSelectedPlans((prev) => ({
            ...prev,
            [carrier]: planWithCarrierName ? allPlans.map((item) => `${carrier}${splitsymbol}${item}`) : allPlans,
        }));
    };

    const handleResetPlans = (carrier: string) => {
        setSelectedPlans((prev) => ({
            ...prev,
            [carrier]: [],
        }));
    };

    return (
        <Modal
            title={`Add insurance for ${state}`}
            open={visible}
            onCancel={onCancel}
            width={800}
            className={s.modal}
            footer={[
                <Button key="cancel" onClick={onCancel}>Cancel</Button>,
                <Button key="save" type="primary" onClick={() => onSave(selectedCarriers, selectedPlans)}>
                    Save
                </Button>,
            ]}
        >
            {
                insuranceList.length === 0 ? (
                    <div className={s.container}>
                        <div className={s.empty}>
                            No insurance carriers found
                        </div>
                    </div>
                ) : (
                    <div className={s.container}>

                        <div className={s.title}>
                            Please select all insurance carriers you&apos;re currently in-network with
                            <span className={s.required}>*</span>
                        </div>

                        <div className={s.carrierColumns}>
                            <div className={s.column}>
                                {sortedCarriers.leftColumn.map((carrier) => (
                                    <Checkbox
                                        key={carrier}
                                        checked={selectedCarriers.includes(carrier)}
                                        onChange={(e) => handleCarrierSelect(carrier, e.target.checked)}
                                    >
                                        {carrier}
                                    </Checkbox>
                                ))}
                            </div>
                            <div className={s.column}>
                                {sortedCarriers.rightColumn.map((carrier) => (
                                    <Checkbox
                                        key={carrier}
                                        checked={selectedCarriers.includes(carrier)}
                                        onChange={(e) => handleCarrierSelect(carrier, e.target.checked)}
                                    >
                                        {carrier}
                                    </Checkbox>
                                ))}
                            </div>
                        </div>

                        {selectedCarriers.length > 0 && (
                            <div className={s.plansSection}>
                                <div className={s.plansTitle}>
                                    Please select plans for each insurance carrier
                                    <div className={s.plansSubtitle}>
                                        The more plans you select, the more patients can find and book with you.
                                    </div>
                                </div>

                                <Collapse
                                    className={s.plansCollapse}
                                    activeKey={expandedCarriers}
                                    onChange={(keys) => setExpandedCarriers(keys as string[])}
                                >
                                    {selectedCarriers.map((carrier) => {
                                        const carrierPlans = insuranceList.find((i) => i.carrier === carrier)?.plans?.filter((e) => !!e) || [];
                                        const selectedCarrierPlans = selectedPlans[carrier] || [];
                                        const midPoint = Math.ceil(carrierPlans.length / 2);

                                        return (
                                            <Panel
                                                key={carrier}
                                                header={carrier}
                                                className={s.panel}
                                                extra={`${selectedCarrierPlans.length} of ${carrierPlans.length} plans`}
                                            >
                                                <div className={s.planHeader}>
                                                    <span>Select the plans you accept</span>
                                                    <div className={s.planActions}>
                                                        <Checkbox
                                                            checked={selectedCarrierPlans.length === carrierPlans.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    handleSelectAllPlans(carrier);
                                                                } else {
                                                                    handleResetPlans(carrier);
                                                                }
                                                            }}
                                                        >
                                                            Select all
                                                        </Checkbox>
                                                        {/* <Button
                                                            type="link"
                                                            onClick={() => handleSelectAllPlans(carrier)}
                                                        >
                                                            Select all
                                                        </Button> */}
                                                        <Button
                                                            type="link"
                                                            onClick={() => handleResetPlans(carrier)}
                                                        >
                                                            Reset
                                                        </Button>
                                                    </div>
                                                </div>

                                                <div className={s.planColumns}>
                                                    <div className={s.column}>
                                                        {carrierPlans.slice(0, midPoint).map((plan) => (
                                                            <Checkbox
                                                                key={plan}
                                                                checked={selectedCarrierPlans.includes(planWithCarrierName ? `${carrier}${splitsymbol}${plan}` : plan)}
                                                                onChange={(e) => handlePlanSelect(carrier, planWithCarrierName ? `${carrier}${splitsymbol}${plan}` : plan, e.target.checked)}
                                                            >
                                                                {plan}
                                                            </Checkbox>
                                                        ))}
                                                    </div>
                                                    <div className={s.column}>
                                                        {carrierPlans.slice(midPoint).map((plan) => (
                                                            <Checkbox
                                                                key={plan}
                                                                checked={selectedCarrierPlans.includes(planWithCarrierName ? `${carrier}${splitsymbol}${plan}` : plan)}
                                                                onChange={(e) => handlePlanSelect(carrier, planWithCarrierName ? `${carrier}${splitsymbol}${plan}` : plan, e.target.checked)}
                                                            >
                                                                {plan}
                                                            </Checkbox>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Panel>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        )}
                    </div>
                )
            }
        </Modal>
    );
};

export default InsuranceSelectionModal;
